import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  ViewChild,
  ElementRef,
} from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  of,
  Subject,
  switchMap,
  tap,
} from 'rxjs';

import { IFilterCBSAs, IFilterCBSAsFormatted } from '@shared/models/filters';
import { AccountPreferencesService } from '@core/services/account-preferences.service';

@UntilDestroy()
@Component({
  selector: 'app-account-setup-markets',
  templateUrl: './markets.component.html',
  styleUrls: [
    '../investor2/investor2.component.scss',
    './markets.component.scss',
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AccountSetupMarketsComponent implements OnInit, OnChanges {
  @Input({ required: true }) error = false;
  @Input({ required: true }) disabled = false;
  @Input() autofocus = false;
  @Input() defaultSelected: IFilterCBSAs[] = [];

  @Input() manageProfile = false;
  @Input() filters = false;

  @Output() selectedChange = new EventEmitter<IFilterCBSAs[]>();

  @ViewChild('cbsaSearchInput', { static: false })
  cbsaSearchInput!: ElementRef<HTMLInputElement>;

  protected loading = false;
  protected searchTerm: string = '';
  protected searchTermSubject = new Subject<string>();
  protected borderError = false;

  protected selected: IFilterCBSAsFormatted[] = [];
  protected showMoreItems = false;
  protected needsCollapse = false;
  protected options: IFilterCBSAsFormatted[] = [];
  private selectedKeySet: Set<string> = new Set<string>();
  private lastSearchedTerm = '';

  constructor(
    private accountPreferencesService: AccountPreferencesService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.updateSelectedFromDefault();
    this.setupSearchSubscription();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['defaultSelected'] && !changes['defaultSelected'].firstChange) {
      this.updateSelectedFromDefault();
    }
  }

  private updateSelectedFromDefault(): void {
    if (this.defaultSelected && this.defaultSelected.length) {
      const seen = new Set<string>();
      this.selected = [];
      for (const cbsa of this.defaultSelected) {
        const key = this.getKey({
          type: cbsa.type,
          preference: cbsa.preference,
        });
        if (seen.has(key)) continue;
        seen.add(key);
        this.selected.push({
          type: cbsa.type,
          preference: cbsa.preference,
          formattedType: this.formatCbsaType(cbsa.type),
          formattedPreference: this.formatCbsaPreference(cbsa.preference),
        });
      }
    } else {
      this.selected = [];
    }
    this.syncSelectedKeySet();
    this.updateOptionSelectionFlags();
    this.checkIfNeedsCollapse();
  }

  private setupSearchSubscription(): void {
    this.searchTermSubject
      .pipe(
        debounceTime(500),
        distinctUntilChanged(),
        // Only show loading if the normalized term will trigger a new search
        tap((term) => {
          // Track the last normalized term that actually passed distinctUntilChanged
          this.lastSearchedTerm = term;
          if (term.length >= 3) {
            this.loading = true;
            this.options = [];
          } else {
            this.loading = false;
            this.options = [];
          }
          this.showBorderError();
          this.cdr.markForCheck();
        }),
        switchMap((term) => {
          if (term.length >= 3) {
            return this.accountPreferencesService
              .searchCbsa({ text: term })
              .pipe(
                tap((res) => {
                  this.options = res.searchCbsa.map((cbsa) => {
                    const key = this.getKey({
                      type: cbsa.type,
                      preference: cbsa.preference,
                    });
                    return {
                      type: cbsa.type,
                      preference: cbsa.preference,
                      formattedType: this.formatCbsaType(cbsa.type),
                      formattedPreference: this.formatCbsaPreference(
                        cbsa.preference
                      ),
                      key,
                      selected: this.selectedKeySet.has(key),
                    };
                  });
                  this.loading = false;
                  this.showBorderError();
                  this.cdr.markForCheck();
                }),
                catchError(() => {
                  this.loading = false;
                  this.showBorderError();
                  this.cdr.markForCheck();
                  return of({ searchCbsa: [] });
                })
              );
          } else {
            this.loading = false;
            this.showBorderError();
            this.cdr.markForCheck();
            return of({ searchCbsa: [] });
          }
        }),
        untilDestroyed(this)
      )
      .subscribe();
  }

  onSearchChange(): void {
    const trimmedValue = this.searchTerm.trim();

    // Immediately reflect loading state in the UI to avoid showing
    // the "no results" message before the debounced search starts.
    if (trimmedValue.length >= 3) {
      // Only switch to loading if this is a new normalized term
      // Different whitespace (e.g., trailing space) should not trigger loading
      if (trimmedValue !== this.lastSearchedTerm) {
        this.loading = true;
        this.options = [];
      }
    } else {
      this.loading = false;
      this.options = [];
    }
    this.showBorderError();
    this.cdr.markForCheck();

    this.searchTermSubject.next(trimmedValue);
  }

  trackByOptionKey = (index: number, option: IFilterCBSAsFormatted): string => {
    return option.key ?? this.getKey(option);
  };

  trackBySelectedKey = (index: number, item: IFilterCBSAsFormatted): string => {
    return this.getKey(item);
  };

  isSelected(option: IFilterCBSAsFormatted): boolean {
    const key = option.key ?? this.getKey(option);
    return this.selectedKeySet.has(key);
  }

  toggleSelected(option: IFilterCBSAsFormatted): void {
    if (this.disabled) return;
    this.showBorderError();
    const key = this.getKey(option);
    const exists = this.selected.some(
      (selected) => this.getKey(selected) === key
    );
    if (exists) {
      this.selected = this.selected.filter(
        (selected) => this.getKey(selected) !== key
      );
    } else {
      this.selected = [
        {
          type: option.type,
          preference: option.preference,
          formattedType: option.formattedType,
          formattedPreference: option.formattedPreference,
        },
        ...this.selected,
      ];
    }
    this.syncSelectedKeySet();
    this.updateOptionSelectionFlags();
    this.selectedChange.emit(this.selected);
    this.checkIfNeedsCollapse();
  }

  removeSelected(option: IFilterCBSAs): void {
    if (this.disabled) return;
    this.showBorderError();
    const key = this.getKey(option);
    this.selected = this.selected.filter(
      (selected) => this.getKey(selected) !== key
    );
    this.syncSelectedKeySet();
    this.updateOptionSelectionFlags();
    this.selectedChange.emit(this.selected);
    this.checkIfNeedsCollapse();
  }

  showBorderError() {
    this.borderError =
      this.error && !this.loading && this.searchTerm.length === 0;
  }

  formatCbsaType(type: string | undefined | null): string {
    if (!type || typeof type !== 'string') return 'Type unknown';

    if (type === 'cbsa' || type === 'CBSA') return 'Metro';
    if (type === 'zip' || type === 'ZIP') return 'Zip';
    if (type === 'county_state' || type === 'COUNTY_STATE') return 'County';

    return type;
  }

  formatCbsaPreference(pref: string | undefined | null): string {
    if (!pref || typeof pref !== 'string') return '';

    const parts = pref.split(',');
    const placesRaw = (parts[0] ?? '').trim();
    const stateRaw = (parts[1] ?? '').trim();

    const normalizeSeparators = (s: string) =>
      s
        .replace(/\s+/g, ' ') // collapse internal spaces
        .replace(/--/g, ',')
        .replace(/-/g, ',');

    const toTitleCase = (s: string) =>
      s
        .toLowerCase()
        .split(/\s+/)
        .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : ''))
        .join(' ');

    // Places: convert hyphens to commas, then title-case each segment
    const placeSegments = normalizeSeparators(placesRaw)
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
      .map(toTitleCase);

    // State(s): convert hyphens to commas, uppercase each token (e.g., DC-VA-MD)
    const stateSegments = normalizeSeparators(stateRaw)
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
      .map((s) => s.toUpperCase());

    const tokens = [...placeSegments, ...stateSegments];
    return tokens.join(', ');
  }

  // Select the entire content of the input when it is focused or clicked
  handleFocus(event: Event) {
    const input = event.target as HTMLInputElement | null;
    if (!input) return;

    // Only select text if the input is empty or if the user is clicking directly on the input
    // Don't select if this is triggered by clicking on search options
    if (input.value.length === 0 || event.type === 'focus') {
      // Defer slightly to ensure focus/DOM updates settle before selecting
      setTimeout(() => {
        try {
          input.select?.();
          // Fallback for some mobile browsers
          if (
            typeof input.selectionStart === 'number' &&
            typeof input.selectionEnd === 'number'
          ) {
            input.setSelectionRange(0, input.value.length);
          }
        } catch {
          // no-op if selection isn't supported
        }
      }, 0);
    }
  }

  // Build a canonical key for equality checks, normalizing type and preference
  private getKey(item: { type: string; preference: string }): string {
    return `${this.normalizeType(item.type)}::${this.normalizePreference(
      item.preference
    )}`;
  }

  private normalizeType(type: string | undefined | null): string {
    const t = (type ?? '').toString().trim().toLowerCase();
    if (t === 'cbsa') return 'cbsa';
    if (t === 'zip') return 'zip';
    if (t === 'county_state' || t === 'county-state') return 'county_state';
    return t;
  }

  private normalizePreference(pref: string | undefined | null): string {
    return (pref ?? '')
      .toString()
      .trim()
      .replace(/\s+/g, ' ') // collapse internal whitespace
      .toLowerCase(); // compare case-insensitively
  }

  private syncSelectedKeySet(): void {
    this.selectedKeySet = new Set(this.selected.map((s) => this.getKey(s)));
  }

  private updateOptionSelectionFlags(): void {
    if (!this.options || !this.options.length) return;
    const set = this.selectedKeySet;
    this.options = this.options.map((o) => ({
      ...o,
      selected: set.has(o.key ?? ''),
    }));
  }

  // Public method to clear the search
  clearSearch(): void {
    this.searchTerm = '';
    this.options = [];
    this.loading = false;
    this.lastSearchedTerm = '';
    this.showBorderError();
    // Trigger the search change to reset the search subject
    this.onSearchChange();
    this.cdr.markForCheck();
  }

  // Toggle show more/less for selected items
  toggleShowMoreItems(): void {
    this.showMoreItems = !this.showMoreItems;
  }

  // Check if the items container needs collapsing
  public checkIfNeedsCollapse(): void {
    // Use setTimeout to ensure DOM is updated
    setTimeout(() => {
      const itemsContainer = document.querySelector(
        '.items-container .items'
      ) as HTMLElement;
      if (itemsContainer) {
        this.needsCollapse = itemsContainer.scrollHeight > 165;
        this.cdr.markForCheck();
      }
    }, 0);
  }

  // Force focus on the search input
  public forceFocus(): void {
    if (this.cbsaSearchInput && this.cbsaSearchInput.nativeElement) {
      // Use setTimeout to ensure the DOM is fully rendered
      setTimeout(() => {
        this.cbsaSearchInput.nativeElement.focus();
        // Also select the text if there's any content
        if (this.cbsaSearchInput.nativeElement.value) {
          this.cbsaSearchInput.nativeElement.select();
        }
      }, 0);
    }
  }
}
